import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import DictationInput from '../../components/DictationInput';
import { toast } from 'sonner';
import {
  Fingerprint, Lock, Users, CheckCircle, Send,
  Loader2, MessageSquare, Sparkles, ArrowRight
} from 'lucide-react';

const PROGRAM_LABELS = { heat: 'HEAT', her: 'HER', youth: 'HEAT for Youth' };
const PROGRAM_COLORS = {
  heat: { bg: 'bg-orange-600', hover: 'hover:bg-orange-700', light: 'bg-orange-50', text: 'text-orange-700', accent: 'border-orange-500' },
  her: { bg: 'bg-pink-600', hover: 'hover:bg-pink-700', light: 'bg-pink-50', text: 'text-pink-700', accent: 'border-pink-500' },
  youth: { bg: 'bg-violet-600', hover: 'hover:bg-violet-700', light: 'bg-violet-50', text: 'text-violet-700', accent: 'border-violet-500' },
};

export default function ClassSignInPage({ program = 'heat' }) {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const colors = PROGRAM_COLORS[program] || PROGRAM_COLORS.heat;
  const label = PROGRAM_LABELS[program] || 'HEAT';

  const [step, setStep] = useState('loading'); // loading, pin_setup, select_facilitator, enter_pin, signed_in, reflecting, follow_up, done
  const [pinSetup, setPinSetup] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [facilitators, setFacilitators] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedFacilitator, setSelectedFacilitator] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [pin, setPin] = useState('');
  const [sessionData, setSessionData] = useState(null);
  const [reflection, setReflection] = useState('');
  const [aiQuestion, setAiQuestion] = useState('');
  const [followUpResponse, setFollowUpResponse] = useState('');
  const [reflectionId, setReflectionId] = useState('');
  const [moduleReflectionPrompt, setModuleReflectionPrompt] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const init = useCallback(async () => {
    try {
      // Check if already in a session today
      const sessionRes = await api.get(`/class/my-session?program=${program}`).catch(() => ({ data: null }));
      if (sessionRes.data) {
        setSessionData(sessionRes.data);
        if (sessionRes.data.module_reflection_prompt) setModuleReflectionPrompt(sessionRes.data.module_reflection_prompt);
        // Check if reflection already submitted
        const refRes = await api.get(`/class/reflections/${sessionRes.data.session_id}`).catch(() => ({ data: [] }));
        const myRef = (refRes.data || []).find(r => r.user_id === user.id);
        if (myRef?.status === 'completed') { setStep('done'); setReflection(myRef.reflection_text); setAiQuestion(myRef.ai_follow_up_question); setFollowUpResponse(myRef.follow_up_response); }
        else if (myRef) { setAiQuestion(myRef.ai_follow_up_question); setReflection(myRef.reflection_text); setStep('follow_up'); }
        else { setStep('reflecting'); }
        return;
      }
      // Check PIN status
      const pinRes = await api.get('/class/pin/status').catch(() => ({ data: { pin_set: false } }));
      if (!pinRes.data.pin_set) { setStep('pin_setup'); }
      else {
        const [facRes, modRes] = await Promise.all([
          api.get(`/class/active-facilitators?program=${program}`).catch(() => ({ data: [] })),
          api.get(`/class/modules?program=${program}`).catch(() => ({ data: [] })),
        ]);
        setFacilitators(facRes.data || []);
        setModules(modRes.data || []);
        setStep('select_facilitator');
      }
    } catch { setStep('select_facilitator'); }
  }, [program, user.id]);

  useEffect(() => { init(); }, [init]);

  const handlePinSetup = async () => {
    if (pinSetup.length < 4) return toast.error('PIN must be at least 4 digits');
    if (pinSetup !== pinConfirm) return toast.error('PINs don\'t match');
    if (!/^\d+$/.test(pinSetup)) return toast.error('PIN must be numbers only');
    setSubmitting(true);
    try {
      await api.post(`/class/pin/setup?pin=${pinSetup}`);
      toast.success('PIN created!');
      const [facRes, modRes] = await Promise.all([
        api.get(`/class/active-facilitators?program=${program}`),
        api.get(`/class/modules?program=${program}`).catch(() => ({ data: [] })),
      ]);
      setFacilitators(facRes.data || []);
      setModules(modRes.data || []);
      setStep('select_facilitator');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to set PIN'); }
    setSubmitting(false);
  };

  const handleCheckIn = async () => {
    if (!selectedFacilitator) return toast.error('Select your facilitator');
    if (!pin || pin.length < 4) return toast.error('Enter your 4-digit PIN');
    setSubmitting(true);
    try {
      const res = await api.post('/class/checkin', { program, facilitator_id: selectedFacilitator, pin, module_id: selectedModule || undefined });
      const selectedMod = modules.find(m => m.id === selectedModule);
      setSessionData({ session_id: res.data.session_id, facilitator_name: res.data.facilitator_name, module_name: selectedMod?.name || '' });
      if (selectedMod?.reflection_prompt) setModuleReflectionPrompt(selectedMod.reflection_prompt);
      toast.success(res.data.message);
      setStep('reflecting');
    } catch (e) { toast.error(e.response?.data?.detail || 'Sign-in failed — check your PIN'); }
    setSubmitting(false);
  };

  const handleReflectionSubmit = async () => {
    if (!reflection.trim()) return toast.error('Share what\'s on your mind');
    setSubmitting(true);
    try {
      const res = await api.post('/class/reflection', { program, session_id: sessionData.session_id, reflection_text: reflection, module_name: sessionData.module_name || '' });
      setAiQuestion(res.data.ai_follow_up);
      setReflectionId(res.data.reflection_id);
      setStep('follow_up');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSubmitting(false);
  };

  const handleFollowUpSubmit = async () => {
    if (!followUpResponse.trim()) return toast.error('Say something — even a few words');
    setSubmitting(true);
    try {
      await api.post('/class/reflection/followup', { program, session_id: sessionData.session_id, original_reflection: reflection, follow_up_response: followUpResponse, ai_question: aiQuestion });
      toast.success('Reflection complete. Respect.');
      setStep('done');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSubmitting(false);
  };

  // --- PIN Setup ---
  if (step === 'loading') return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;

  if (step === 'pin_setup') return (
    <div className="max-w-md mx-auto space-y-6 pt-8" data-testid="pin-setup">
      <div className="text-center">
        <div className={`w-16 h-16 ${colors.light} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
          <Lock className={`w-8 h-8 ${colors.text}`} />
        </div>
        <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>Create Your {label} PIN</h1>
        <p className="text-sm text-muted-foreground mt-1">You'll use this PIN to sign into every class session</p>
      </div>
      <Card className={`border-0 shadow-sm rounded-2xl border-t-4 ${colors.accent}`}>
        <CardContent className="p-6 space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Create PIN (4+ digits)</label>
            <Input type="password" inputMode="numeric" maxLength={8} value={pinSetup} onChange={e => setPinSetup(e.target.value.replace(/\D/g, ''))} placeholder="Enter 4+ digit PIN" className="text-center text-2xl tracking-[0.5em] h-14 rounded-xl" data-testid="pin-setup-input" />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Confirm PIN</label>
            <Input type="password" inputMode="numeric" maxLength={8} value={pinConfirm} onChange={e => setPinConfirm(e.target.value.replace(/\D/g, ''))} placeholder="Confirm PIN" className="text-center text-2xl tracking-[0.5em] h-14 rounded-xl" data-testid="pin-confirm-input" />
          </div>
          <Button onClick={handlePinSetup} disabled={submitting} className={`w-full ${colors.bg} ${colors.hover} text-white rounded-full h-12 text-base gap-2`} data-testid="pin-setup-submit">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} Set My PIN
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  // --- Select Facilitator & Sign In ---
  if (step === 'select_facilitator' || step === 'enter_pin') return (
    <div className="max-w-md mx-auto space-y-6 pt-8" data-testid="class-signin">
      <div className="text-center">
        <div className={`w-16 h-16 ${colors.light} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
          <Fingerprint className={`w-8 h-8 ${colors.text}`} />
        </div>
        <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>{label} Class Sign-In</h1>
        <p className="text-sm text-muted-foreground mt-1">Select your facilitator and enter your PIN</p>
      </div>
      <Card className={`border-0 shadow-sm rounded-2xl border-t-4 ${colors.accent}`}>
        <CardContent className="p-6 space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Who's teaching today?</label>
            <Select value={selectedFacilitator} onValueChange={v => { setSelectedFacilitator(v); }}>
              <SelectTrigger className="rounded-xl h-12" data-testid="facilitator-select">
                <SelectValue placeholder="Select facilitator" />
              </SelectTrigger>
              <SelectContent>
                {facilitators.map(f => <SelectItem key={f.id} value={f.id}>{f.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {selectedFacilitator && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Today's session topic</label>
              <Select value={selectedModule} onValueChange={v => { setSelectedModule(v); setStep('enter_pin'); }}>
                <SelectTrigger className="rounded-xl h-12" data-testid="module-select">
                  <SelectValue placeholder="Select module" />
                </SelectTrigger>
                <SelectContent>
                  {modules.map(m => <SelectItem key={m.id} value={m.id}>{m.name}{m.section ? ` (${m.section})` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {step === 'enter_pin' && (
            <>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Your PIN</label>
                <Input type="password" inputMode="numeric" maxLength={8} value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ''))} placeholder="Enter PIN" className="text-center text-2xl tracking-[0.5em] h-14 rounded-xl" data-testid="signin-pin-input" autoFocus />
              </div>
              <Button onClick={handleCheckIn} disabled={submitting || !pin} className={`w-full ${colors.bg} ${colors.hover} text-white rounded-full h-12 text-base gap-2`} data-testid="signin-submit">
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />} Sign In
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // --- Reflection ---
  if (step === 'reflecting') return (
    <div className="max-w-lg mx-auto space-y-6 pt-4" data-testid="class-reflection">
      <div className="text-center">
        <Badge className={`${colors.light} ${colors.text} border-0 mb-2`}><CheckCircle size={12} className="mr-1" /> Signed In</Badge>
        <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>Time to Reflect</h1>
        {sessionData?.module_name && <p className="text-sm font-medium mt-1">{sessionData.module_name}</p>}
        <p className="text-sm text-muted-foreground mt-2">
          {moduleReflectionPrompt || (program === 'youth'
            ? 'Name one thing that stood out to you from today\'s session.'
            : 'Name one thing that stood out to you from today\'s session.')}
        </p>
        {sessionData?.facilitator_name && <p className="text-xs text-muted-foreground mt-1">Facilitator: {sessionData.facilitator_name}</p>}
      </div>
      <Card className={`border-0 shadow-sm rounded-2xl border-t-4 ${colors.accent}`}>
        <CardContent className="p-6 space-y-4">
          <DictationInput
            value={reflection}
            onChange={v => setReflection(v)}
            rows={4}
            placeholder={program === 'youth'
              ? 'What stood out to you today? What made you think?'
              : 'What stood out? What challenged you? What are you taking with you?'}
            data-testid="reflection-input"
            showCleanup={false}
          />
          <Button onClick={handleReflectionSubmit} disabled={submitting || !reflection.trim()} className={`w-full ${colors.bg} ${colors.hover} text-white rounded-full h-12 gap-2`} data-testid="reflection-submit">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Submit Reflection
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  // --- AI Follow-Up ---
  if (step === 'follow_up') return (
    <div className="max-w-lg mx-auto space-y-6 pt-4" data-testid="class-followup">
      <div className="text-center">
        <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>Go Deeper</h1>
        <p className="text-sm text-muted-foreground mt-1">Your reflection got us thinking. Answer this one.</p>
      </div>
      {/* Show their reflection */}
      <Card className="border-0 shadow-sm rounded-2xl bg-slate-50">
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground mb-1">Your reflection:</p>
          <p className="text-sm italic text-slate-700">"{reflection}"</p>
        </CardContent>
      </Card>
      {/* AI question */}
      <Card className={`border-0 shadow-sm rounded-2xl border-l-4 ${colors.accent}`}>
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className={`w-9 h-9 rounded-full ${colors.light} flex items-center justify-center shrink-0`}>
              <Sparkles size={16} className={colors.text} />
            </div>
            <div>
              <p className="text-sm font-medium" data-testid="ai-followup-question">{aiQuestion}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card className={`border-0 shadow-sm rounded-2xl border-t-4 ${colors.accent}`}>
        <CardContent className="p-6 space-y-4">
          <DictationInput
            value={followUpResponse}
            onChange={v => setFollowUpResponse(v)}
            rows={3}
            placeholder="Speak your truth..."
            data-testid="followup-input"
            showCleanup={false}
          />
          <Button onClick={handleFollowUpSubmit} disabled={submitting || !followUpResponse.trim()} className={`w-full ${colors.bg} ${colors.hover} text-white rounded-full h-12 gap-2`} data-testid="followup-submit">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} Finish
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  // --- Done ---
  return (
    <div className="max-w-md mx-auto space-y-6 pt-8 text-center" data-testid="class-done">
      <div className={`w-20 h-20 ${colors.light} rounded-full flex items-center justify-center mx-auto`}>
        <CheckCircle className={`w-10 h-10 ${colors.text}`} />
      </div>
      <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>You're Good</h1>
      <p className="text-sm text-muted-foreground">Attendance logged. Reflection submitted. Keep showing up.</p>
      <Card className="border-0 shadow-sm rounded-2xl bg-slate-50 text-left">
        <CardContent className="p-4 space-y-2 text-sm">
          <p><span className="text-muted-foreground">Your reflection:</span> <span className="italic">"{reflection}"</span></p>
          {aiQuestion && <p><span className="text-muted-foreground">Follow-up:</span> {aiQuestion}</p>}
          {followUpResponse && <p><span className="text-muted-foreground">Your response:</span> <span className="italic">"{followUpResponse}"</span></p>}
        </CardContent>
      </Card>
    </div>
  );
}
